/**
 * Shared shell for canvas routes: React Flow store (keyed by workspace id),
 * route-scoped `canvasId`, floating chat, `CanvasLiveblocksProvider` (room + document),
 * UI shell, `CanvasProcessProvider` (reads `canvasId` / nodes from context), and
 * `CanvasCommentsProvider`, which sits above the page so the entry points in
 * `CanvasPage`'s own body can place a draft pin.
 */

import { ReactFlowProvider } from "@xyflow/react";
import type { ComponentType, ReactNode } from "react";
import { useParams } from "react-router";
import { CanvasCommentsProvider } from "../comments/CanvasCommentsContext";
import { CanvasChromeEnterAnimationProvider } from "../hooks/useCanvasChromeEnterAnimation";
import { CanvasLiveblocksProvider } from "./CanvasContext";
import {
  CanvasFloatingChatProvider,
  CanvasFloatingChatStubProvider,
} from "./CanvasFloatingChatContext";
import { CanvasLocalFilePreviewProvider } from "./CanvasLocalFilePreviewContext";
import {
  CanvasProcessProvider,
  CanvasProcessStubProvider,
} from "./CanvasProcessContext";
import { CanvasReplayProvider } from "./CanvasReplayContext";
import { CanvasRouteProvider } from "./CanvasRouteContext";
import { CanvasSectionPreviewProvider } from "./CanvasSectionPreviewContext";
import { CanvasSelectionCountProvider } from "./CanvasSelectionCountContext";
import { CanvasUiProvider } from "./CanvasUiContext";
const CANVAS_PROVIDER_KEY_NONE = "__none__";

export type CanvasProviderUiVariant = "interactive" | "capture" | "view";

export function CanvasProvider({
  children,
  canvasId: canvasIdFromProps,
  uiVariant = "interactive",
}: {
  children: ReactNode;
  /** When set (e.g. `canvas-capture.html?canvasId=`), overrides `:canvasId` from the router. */
  canvasId?: string;
  /** `capture` uses frozen UI defaults for thumbnail / headless capture. */
  uiVariant?: CanvasProviderUiVariant;
}) {
  const { canvasId: canvasIdFromParams } = useParams<{ canvasId: string }>();
  const canvasKey =
    canvasIdFromProps ?? canvasIdFromParams ?? CANVAS_PROVIDER_KEY_NONE;
  // A capture never chats or dispatches; the real providers would fetch the
  // canvas's flow sessions and harness capabilities for nothing.
  const capture = uiVariant === "capture";
  const FloatingChatProvider = capture
    ? CanvasFloatingChatStubProvider
    : CanvasFloatingChatProvider;
  const ProcessProvider = capture
    ? CanvasProcessStubProvider
    : CanvasProcessProvider;

  return (
    <CanvasChromeEnterAnimationProvider>
      <ReactFlowProvider key={canvasKey}>
        <CanvasRouteProvider canvasId={canvasIdFromProps}>
          <CanvasReplayProvider>
            <FloatingChatProvider>
              <CanvasLiveblocksProvider uiVariant={uiVariant}>
                <CanvasUiProvider variant={uiVariant}>
                  <CanvasSectionPreviewProvider variant={uiVariant}>
                    <CanvasSelectionCountProvider>
                      <CanvasLocalFilePreviewProvider>
                        <ProcessProvider>
                          <CanvasCommentsProvider>
                            {children}
                          </CanvasCommentsProvider>
                        </ProcessProvider>
                      </CanvasLocalFilePreviewProvider>
                    </CanvasSelectionCountProvider>
                  </CanvasSectionPreviewProvider>
                </CanvasUiProvider>
              </CanvasLiveblocksProvider>
            </FloatingChatProvider>
          </CanvasReplayProvider>
        </CanvasRouteProvider>
      </ReactFlowProvider>
    </CanvasChromeEnterAnimationProvider>
  );
}

/** Optional props consumed by the HOC wrapper (not forwarded to the wrapped component). */
export type WithCanvasProviderProps = {
  /** Overrides router `:canvasId` (e.g. hero capture query entry). */
  canvasId?: string;
};

/**
 * Wraps a canvas page with `CanvasProvider` so route modules can export a single component.
 */
export function withCanvasProvider<P extends object>(
  Wrapped: ComponentType<P>,
  options: { uiVariant: CanvasProviderUiVariant },
): ComponentType<P & WithCanvasProviderProps> {
  function WithCanvasProvider(props: P & WithCanvasProviderProps) {
    const { canvasId: canvasIdOverride, ...rest } = props;
    return (
      <CanvasProvider canvasId={canvasIdOverride} uiVariant={options.uiVariant}>
        <Wrapped {...(rest as P)} />
      </CanvasProvider>
    );
  }
  const wrappedName = Wrapped.displayName || Wrapped.name || "Component";
  WithCanvasProvider.displayName = `WithCanvasProvider(${wrappedName})`;
  return WithCanvasProvider;
}
